import { useCallback, useEffect, useState } from "react";
import { History } from "lucide-react";
import { getRadarZero, revertRadarZero, setRadarZero } from "services/experimentApi";

export const RADAR_ZERO_HELP =
  "How far below the pipe opening this radar really measures from, found by a calibration campaign in the " +
  "Experiment tab (each radar differs a little, about 2 cm). Water level = pipe opening to ground − radar zero − " +
  "distance. Changing it only affects new readings.";

const ACTION_TEXT = { apply: "Applied from campaign", manual: "Set by hand", clear: "Cleared", revert: "Reverted" };

const cm = (value) => (value == null ? "—" : `${Number(value).toFixed(2)} cm`);

function revisionText(revision) {
  const action = ACTION_TEXT[revision.action] || revision.action;
  const campaign = revision.stats?.campaign ? ` #${revision.stats.campaign}` : "";
  return `${action}${revision.action === "apply" ? campaign : ""}`;
}

/**
 * A radar node's radar zero: the value, where it came from, its history, and (staff only) setting it by hand,
 * clearing it or reverting the latest change. Applying one from a campaign is on the campaign's result card.
 *
 * Props: deviceMac; isStaff; reloadKey (change it to reload); onChanged(device payload); bare (no box or heading, for
 * a page that already gives it a titled card).
 */
export default function RadarZeroField({ deviceMac, isStaff, reloadKey, onChanged, bare = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!deviceMac) return;
    try {
      setData(await getRadarZero(deviceMac));
      setError("");
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [deviceMac]);
  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const current = data?.radar_zero_cm ?? null;
  const revisions = data?.revisions || [];
  const latest = revisions[0];

  const run = async (call, done) => {
    setBusy(true);
    setMessage("");
    try {
      const result = await call();
      setData(result);
      setManual("");
      setMessage(done);
      onChanged?.(result);
    } catch (requestError) {
      setMessage(requestError.message);
    } finally {
      setBusy(false);
    }
  };
  const setByHand = () => {
    const value = Number(manual);
    if (manual === "" || !Number.isFinite(value)) {
      setMessage("Enter the radar zero in cm below the pipe opening (e.g. 2.21).");
      return;
    }
    run(() => setRadarZero(deviceMac, { radarZeroCm: value, expected: current }), "Saved.");
  };

  return (
    <div
      className={bare ? "flex flex-col gap-2" : "flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3"}
      data-testid="radar-zero-field"
    >
      {!bare && (
        <div>
          <p className="text-sm font-semibold text-slate-800">Radar zero (cm below the pipe opening)</p>
          <p className="text-xs text-gray-500">{RADAR_ZERO_HELP}</p>
        </div>
      )}
      {error && <p role="alert" className="text-xs text-red-700">{error}</p>}
      <div className="rounded-lg bg-slate-50 p-2 text-sm">
        <span className="font-semibold text-slate-900" data-testid="radar-zero-current">
          {current == null ? "Not calibrated yet" : cm(current)}
        </span>
        {latest && current != null && (
          <span className="block text-xs text-slate-500">
            {revisionText(latest)}
            {latest.created_by ? `, by ${latest.created_by}` : ""}, {new Date(latest.created_at).toLocaleString()}
          </span>
        )}
        {current == null && (
          <span className="block text-xs text-slate-500">
            Run a calibration campaign in the Experiment tab, then apply the radar zero it finds.
          </span>
        )}
      </div>

      {isStaff && (
        <div className="flex flex-wrap items-end gap-2 text-xs">
          <label className="flex flex-col gap-1">
            <span className="text-slate-600">Set by hand (cm)</span>
            <input
              type="number"
              step="0.01"
              value={manual}
              onChange={(event) => setManual(event.target.value)}
              aria-label="Radar zero by hand (cm)"
              className="w-28 rounded border border-slate-300 px-2 py-1"
            />
          </label>
          <button type="button" disabled={busy} onClick={setByHand} className="rounded bg-slate-800 px-3 py-1.5 font-semibold text-white disabled:opacity-50">
            Save
          </button>
          {current != null && (
            <button
              type="button"
              disabled={busy}
              onClick={() => run(() => setRadarZero(deviceMac, { radarZeroCm: null, expected: current }), "Cleared.")}
              className="rounded border border-slate-300 px-3 py-1.5 font-semibold text-slate-700 disabled:opacity-50"
            >
              Clear
            </button>
          )}
        </div>
      )}
      {message && <p className="text-xs text-slate-700" role="status">{message}</p>}

      {revisions.length > 0 && (
        <details className="text-xs text-slate-600">
          <summary className="flex cursor-pointer items-center gap-1">
            <History className="h-3.5 w-3.5" aria-hidden="true" /> History ({revisions.length})
          </summary>
          <ul className="mt-1 flex flex-col gap-1">
            {revisions.map((revision, index) => (
              <li key={revision.id} className="flex flex-wrap items-center gap-2">
                <span>
                  {new Date(revision.created_at).toLocaleString()} · {revisionText(revision)} ·{" "}
                  {cm(revision.old_value?.radar_zero_cm)} → {cm(revision.new_value?.radar_zero_cm)}
                  {revision.created_by ? ` · ${revision.created_by}` : ""}
                </span>
                {isStaff && index === 0 && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => run(() => revertRadarZero(revision.id), "Reverted.")}
                    className="rounded border border-slate-300 px-2 py-0.5 font-semibold text-slate-700 disabled:opacity-50"
                  >
                    Revert
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
